import { useAsyncEffect } from "@rsm-hcd/javascript-react";
import Anchor from "atoms/anchors/anchor";
import Button from "atoms/buttons/button";
import { ButtonStyles } from "atoms/constants/button-styles";
import { HeadingPriority } from "atoms/constants/heading-priority";
import Heading from "atoms/typography/heading";
import Paragraph from "atoms/typography/paragraph";
import AlertLevels from "constants/alert-levels";
import UserConfigurationErrorKeys from "constants/user-configuration-error-keys";
import { List } from "immutable";
import { siteMap } from "internal-sitemap";
import { uniqBy } from "lodash";
import BookRecord from "models/view-models/book-record";
import PublicationRecord from "models/view-models/publication-record";
import AlertBanner from "molecules/alerts/alert-banner";
import PublicationCardSimple from "molecules/cards/publication-card-simple";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";
import OnboardingLayout from "templates/onboarding-layout";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { useHeaderData } from "utilities/contexts/use-header-data-context";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import usePublicationFavorites from "utilities/hooks/domain/user-publication-favorites/use-publication-favorites";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import { useLocalization } from "utilities/hooks/use-localization";
import usePageErrors from "utilities/hooks/use-page-errors";
import useUnauthorizedResult from "utilities/hooks/use-unauthorized-result";
import BookService from "utilities/services/books/book-service";
import PublicationService from "utilities/services/publications/publication-service";
import UserService from "utilities/services/users/user-service";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface SelectFavoritesPageProps {}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const CSS_CLASS_NAME = "c-select-favorites";
const PUBLICATIONS_CLASS_NAME = `${CSS_CLASS_NAME}__publications`;
const TAKE = 5;

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const SelectFavoritesPage: React.FC<SelectFavoritesPageProps> = (
    props: SelectFavoritesPageProps
) => {
    const { t } = useLocalization();
    const history = useHistory();
    const { globalState } = useGlobalState();
    const currentUserId = globalState.currentIdentity?.userId();
    const anchorDashboardText = t("selectFavoritesPage-nextButton");
    const bannerText = t("selectFavoritesPage-banner");
    const descriptionFeaturedPublications = t(
        "selectFavoritesPage-description-featuredPublications"
    );
    const heading = t("selectFavoritesPage-header");
    const showLessText = t("showLess");
    const showMoreText = t("showMore");

    // -----------------------------------------------------------------------------------------
    // #region Hooks
    // -----------------------------------------------------------------------------------------

    const isMobile = useBreakpoint(
        Breakpoints.Phone,
        BreakpointComparer.MaxWidth
    );

    const [descriptionParagraph, setDescriptionParagraph] =
        useState<string>("");
    const [publications, setPublications] = useState<Array<PublicationRecord>>(
        []
    );
    const [showAll, setShowAll] = useState(true);
    const { handlePageLoadError, pageErrors } = usePageErrors();
    const { favorites, setFavorites } = usePublicationFavorites();
    const { patch: completedOnboardingFavorites } =
        UserService.useOnboardingFavoritesCompletedOn();
    const [completedOnboarding, setCompletedOnboarding] = useState(false);

    const { list: listPublicationApi } = PublicationService.useList();
    const { list: listBooksApi } = BookService.useList();
    const [featuredBooks, setFeaturedBooks] =
        useState<List<BookRecord>>(List());

    useHeaderData({
        title: heading,
    });

    useUnauthorizedResult(
        UserConfigurationErrorKeys.ERROR_FAVORITES_NOT_INITIALIZED,
        UserConfigurationErrorKeys.ERROR_TEAM_NOT_INITIALIZED
    );

    const findFeaturedPublications = useCallback(
        async (
            featuredBooks: List<BookRecord>
        ): Promise<Array<PublicationRecord>> => {
            try {
                const response = await listPublicationApi();
                const allPublications = response.resultObjects ?? [];
                const featuredPublications = allPublications.filter(
                    (p: PublicationRecord) => {
                        return featuredBooks.some(
                            (b: BookRecord) =>
                                b.code === p.code && b.edition === p.edition
                        );
                    }
                );
                return featuredPublications;
            } catch (result) {
                handlePageLoadError(result);
                return [];
            }
        },
        [handlePageLoadError, listPublicationApi]
    );

    useEffect(() => {
        const completeOnboardingFavorites = async () => {
            try {
                await completedOnboardingFavorites({
                    id: currentUserId!,
                });
                setCompletedOnboarding(true);
            } catch (err) {
                handlePageLoadError(err);
            }
        };

        completeOnboardingFavorites();
    }, []);

    useAsyncEffect(
        async function getPublications() {
            if (!completedOnboarding) return;
            const featuredBooksResponse = await listBooksApi({ isNew: true });
            const featuredBooksList = List(featuredBooksResponse.resultObjects);
            setFeaturedBooks(featuredBooksList);

            if (featuredBooksList == null) return;

            const publications =
                await findFeaturedPublications(featuredBooksList);
            setPublications(publications);
            setDescriptionParagraph(descriptionFeaturedPublications);
        },
        [completedOnboarding]
    );
    // #endregion Hooks

    // -----------------------------------------------------------------------------------------
    // #region Handlers
    // -----------------------------------------------------------------------------------------

    const handleShowAllToggle = () => setShowAll((prevState) => !prevState);

    // #endregion Handlers

    // -----------------------------------------------------------------------------------------
    // #region Transforms
    // ----------------------------------------------------------------------------------------

    const findFeaturedTopicBooks = publications.filter((p) =>
        featuredBooks.some((f) => p.code === f.code && p.edition === f.edition)
    );
    const publicationsWithFeaturedFirst = uniqBy(
        [...findFeaturedTopicBooks, ...publications],
        "id"
    );
    const availablePublications =
        showAll && !isMobile
            ? publicationsWithFeaturedFirst.slice(0, TAKE)
            : publicationsWithFeaturedFirst;

    const buttonText = showAll ? showMoreText : showLessText;
    const displayShowAllText = publicationsWithFeaturedFirst.length > TAKE;

    const handleContinueToDashboard = async () => {
        history.push(siteMap.dashboards.user);
    };
    // #endregion Transforms

    // -----------------------------------------------------------------------------------------
    // #region Render
    // -----------------------------------------------------------------------------------------

    return (
        <OnboardingLayout>
            <div className={CSS_CLASS_NAME}>
                <Heading
                    cssClassName={`${CSS_CLASS_NAME}__title`}
                    priority={HeadingPriority.One}>
                    {heading}
                </Heading>
                <Paragraph cssClassName={`${CSS_CLASS_NAME}__description`}>
                    {descriptionParagraph}
                </Paragraph>
                <div className={PUBLICATIONS_CLASS_NAME}>
                    <div className={`${PUBLICATIONS_CLASS_NAME}__list`}>
                        {availablePublications.map(
                            (publication: PublicationRecord) => {
                                return (
                                    <PublicationCardSimple
                                        favorites={favorites}
                                        key={publication.id}
                                        onChangeFavorites={setFavorites}
                                        publication={publication}
                                    />
                                );
                            }
                        )}
                    </div>
                    {displayShowAllText && (
                        <Button
                            cssClassName={`${PUBLICATIONS_CLASS_NAME}__show-more-button`}
                            style={ButtonStyles.Anchor}
                            onClick={handleShowAllToggle}>
                            {buttonText}
                        </Button>
                    )}
                </div>
                <AlertBanner alertLevel={AlertLevels.Info}>
                    {bannerText}
                </AlertBanner>
                <div className={`${CSS_CLASS_NAME}__divider`}></div>
                <div className={`${CSS_CLASS_NAME}__footer`}>
                    <Anchor
                        dataTestId="go-to-dashboard"
                        ariaLabel="Dashboard"
                        onClick={handleContinueToDashboard}
                        to={siteMap.dashboards.user}
                        title={anchorDashboardText}>
                        {anchorDashboardText}
                    </Anchor>
                </div>
            </div>
        </OnboardingLayout>
    );

    // #endregion Render
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default SelectFavoritesPage;

// #endregion Exports
